// Copyright (c) Docugami, Inc. All rights reserved.

import { dgFontFamily } from 'components/common/Fonts'
import { BasicIconProps } from "components/work_path/IconRenderer";
import { emptyColor, greyText } from 'components/shell/presentation/AppStyles'
import { useDgIcons } from 'hooks/dgIcons'
import { Icon } from "@fluentui/react"
import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { ShowAllButton } from './ExplorerStyles'
import { NavigationGroup } from './ExplorerTypes'
import { NavigationItemList, NavigationListItem } from './NavigationItemList'
import { useBooleanStoredState } from 'hooks/BooleanStoredState'
import { useSelector } from 'react-redux'
import { AppState } from 'store'
import { IExplorerStatusItem } from 'hooks/ExplorerStatus'
import { NavigationItemGroupStatus } from './NavigationItemGroupStatus'

const ItemGroupContainer = styled.div`
    display: flex;
    flex-direction: column;
    span {
        user-select: none;
    }
`

const ContentContainer = styled.div<{ isOpen: boolean, maxHeight: number }>`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    transition: max-height .2s ease-out;
    overflow: hidden;
    max-height: ${props => props.isOpen ? `${props.maxHeight}px` : "0px"};
`

const ItemListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 5px 8px 5px 7px;
    box-sizing: border-box;
`

const ShowAllButtonContainer = styled.div`
    text-align: center;
    align-self: center;
    justify-self: center;
`

interface NavigationItemGroupProps {
    title: string;
    icon?: BasicIconProps;
    items: Array<NavigationListItem>;
    group: NavigationGroup;
    collapsedItemCount: number;
    selectedId?: string;
    emptyPlaceholder?: JSX.Element;
    defaultExpanded?: boolean;
    onExpand?: () => void;
    statusItems: IExplorerStatusItem[];
}

export const NavigationItemGroup = (props: NavigationItemGroupProps): JSX.Element => {
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id)
    const boolKey = useMemo(() => !!orgId ? `${orgId}:DocExplorerGroup:${props.group}` : '', [props.group, orgId]);
    const [isExpanded, setIsExpanded] = useBooleanStoredState(boolKey, props.defaultExpanded);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        // If selection changes to an item owned by this group, make sure the group expands
        const isOwnedByGroup = !!props.selectedId ? props.items.findIndex(x => x.id === props.selectedId) !== -1 : false;
        if (isOwnedByGroup) {
            setIsExpanded(true);
        }
    },
        [props.selectedId, props.items, setIsExpanded]);

    function onExpand() {
        const newExpanded = !isExpanded;
        if (newExpanded && !!props.onExpand) {
            props.onExpand();
        }

        setIsExpanded(newExpanded);
    }

    const isShowAllVisible = useMemo(() => props.items.length > props.collapsedItemCount, [props.collapsedItemCount, props.items]);
    const isPlaceholderVisible = useMemo(() => !!props.emptyPlaceholder && props.items.length === 0, [props.emptyPlaceholder, props.items]);

    // In order to animate the height of the groups, we need to be able to calculate an upper bound on the max height
    // of the content shown here.  There are helper libraries with numerous tricks to attempt to measure elements dynamically,
    // but for now we will stick with this simplistic approach.  If the group gets much more complex we can consider other options.
    const maxHeight = useMemo(() => {
        if (isPlaceholderVisible) {
            return 200;
        }

        // Visible list items
        const visibleItemCount = showAll ? props.items.length : Math.min(props.items.length, props.collapsedItemCount);
        let value = visibleItemCount * 40;
        if (isShowAllVisible) {
            // Show all button
            value += 20;
        }

        return value;
    }, [props.items, isShowAllVisible, showAll, isPlaceholderVisible, props.collapsedItemCount]);

    return (
        <ItemGroupContainer>
            <NavigationItemGroupHeader title={props.title} icon={props.icon} isExpanded={isExpanded} onClick={onExpand} />
            <NavigationItemGroupStatus items={props.statusItems} />
            <ContentContainer isOpen={isExpanded} maxHeight={maxHeight}>
                {isPlaceholderVisible && props.emptyPlaceholder}
                <ItemListContainer>
                    <NavigationItemList items={showAll ? props.items : props.items.slice(0, Math.min(props.items.length, props.collapsedItemCount))} selectedId={props.selectedId} />
                </ItemListContainer>
                <ShowAllButtonContainer hidden={!isShowAllVisible}>
                    <ShowAllButton showAll={showAll} onClick={() => setShowAll(x => !x)} />
                </ShowAllButtonContainer>
            </ContentContainer>
        </ItemGroupContainer>
    )
}

export const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 0px 23px 0px 13px;
    box-sizing: border-box;
    gap: 10px;
    background: ${emptyColor};
    height: 40px;
    cursor: pointer;
`

const DocExplorerNavigationItemGroupLabel = styled.span`
    font-family: ${dgFontFamily};
    font-size: 16px;
    font-weight: 500;
    color: ${greyText};
`;

const LabelContainer = styled.div`
    flex: 1;
`

const iconRotationTransform = css`
    transform: rotate(90deg);
`;

export const IconContainer = styled.div<{ isExpanded: boolean }>`
    display: flex;
    font-size: 12px;
    color: ${greyText};
    ${props => props.isExpanded && iconRotationTransform};
    transition: all .08s ease-out;
    user-select: none;
`

interface NavigationItemGroupHeaderProps {
    title: string;
    isExpanded: boolean;
    icon?: BasicIconProps;
    onClick: () => void;
}

const NavigationItemGroupHeader = (props: NavigationItemGroupHeaderProps): JSX.Element => {
    const icons = useDgIcons();
    
    function getIcon() {
        if (!props.icon) {
            return;
        }

        const icon = icons.icon(
            props.icon.name,
            '',
            props.icon.height,
            props.icon.width,
            greyText,
            '');

        return <div>{icon}</div>
    }

    return (
        <HeaderContainer onClick={props.onClick}>
            {getIcon()}
            <LabelContainer>
                <DocExplorerNavigationItemGroupLabel>{props.title.toUpperCase()}</DocExplorerNavigationItemGroupLabel>
            </LabelContainer>
            <IconContainer isExpanded={props.isExpanded}>
                <Icon iconName={"ChevronRight"} />
            </IconContainer>
        </HeaderContainer>
    )
}
